import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { camelizeKeys } from "humps";
import dayjs from "dayjs";
import { SectionType } from "../pages/HomepageSections";
import {
  logout,
  selectAccessToken,
  selectRefreshToken,
  selectTokenExpiresAt,
  setCredentials,
} from "../slices/auth";
import { RootState } from "../store";
import {
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  SetPasswordRequest,
  StandardResponse,
  MeResponse,
  Section,
  ChangePasswordRequest,
  ApiRegion,
  CustomSection,
  SectionLayout,
  HomepageSectionTab,
  RegionOption,
  CreateTabRequest,
  AddEntryRequest,
  EditEntryRequest,
  EntryTabIcon,
  EntryTabIconColor,
  AddBlogArticleRequest,
  EditBlogArticleRequest,
  BlogArticle,
  GuideEntry,
  GuideEntryParams,
  GuideEntryPayload,
  AddArticleCategoryRequest,
  ArticleCategory,
  AddClientRequest,
  EditClientRequest,
  Client,
  AddClientNoteRequest,
  EditClientNoteRequest,
  ClientNote,
  AddClientTourOfferRequest,
  ClientTourOffer,
  ClientReservation,
  ReservationsStatuses,
  AddClientReservation,
  EditClientReservation,
  EditClientTourOfferRequest,
  AddAppUser,
  EditAppUser,
  AppUser,
  AddQuiz,
  EditQuiz,
  Quiz,
  AddQuestion,
  EditQuestion,
  Question,
  AddAward,
  EditAward,
  Award,
  AddReward,
  EditReward,
  Reward,
  AddMobileOffer,
  EditMobileOffer,
  MobileOffer,
  SolvedQuiz,
  EditArticleCategoryRequest,
  RefetchSection,
  Consultation,
  AddConsultation,
  EditConsultation,
  Prize,
  AddPrize,
  PaymentStatus,
  BookCancel,
  PaymentRest,
  BookCancelResponse,
  IBlogArticleParams,
  FetchBaseQueryMeta,
  IBaseSearchParams,
  INameAndEmailSearchParams,
  IReservationsSearchParams,
  PushToAll,
  ISelectedAwards,
  SelectedAwards,
  ChatSettings,
  ChatHandoff,
  ChatHandoffListParams,
  ChatHandoffsResponse,
  VerifiedHotel,
  VerifiedHotelPayload,
  HotelSourceResult,
  ShortLink,
  ShortLinkPayload,
  ShortLinkStats,
} from "./api.types";
import { cleanObject } from "../utils/helpers";
import { PAGINATION_LIMITS } from "./pagination.constants";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || "/api/",
  fetchFn: fetch,
  prepareHeaders: async (headers, { getState }) => {
    const token = selectAccessToken(getState() as RootState);
    if (token) {
      headers.set("Authorization", `${token}`);
    }
    return headers;
  },
});

type RefreshResult = { data: LoginResponse } | { error: FetchBaseQueryError };
let refreshPromise: Promise<RefreshResult> | null = null;

const TOKEN_EXPIRY_BUFFER_MS = 30 * 1000;

const isTokenExpired = (expiresAt: number | null) => {
  if (!expiresAt) {
    return false;
  }

  return Date.now() + TOKEN_EXPIRY_BUFFER_MS >= expiresAt;
};

const getExpiresInSeconds = (
  result: { data?: unknown; meta?: FetchBaseQueryMeta } | null,
) => {
  if (!result) {
    return null;
  }

  const headerValue = result.meta?.response?.headers.get("X-Token-Expires-In");
  const headerSeconds = headerValue ? Number(headerValue) : null;
  const data = result.data as {
    expires_in?: number;
    expiresIn?: number;
  } | null;
  const bodySeconds =
    typeof data?.expires_in === "number"
      ? data.expires_in
      : typeof data?.expiresIn === "number"
      ? data.expiresIn
      : null;

  return typeof headerSeconds === "number" && !Number.isNaN(headerSeconds)
    ? headerSeconds
    : bodySeconds;
};

const refreshAccessToken = async (
  api: Parameters<
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>
  >[1],
  extraOptions: Parameters<
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>
  >[2],
) => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshResult = await baseQuery(
        {
          url: "/admin/sessions",
          method: "PATCH",
          body: {
            refresh_token: selectRefreshToken(api.getState() as RootState),
          },
        },
        api,
        extraOptions,
      );

      if (refreshResult.data) {
        const expiresIn = getExpiresInSeconds(refreshResult);
        api.dispatch(
          setCredentials({
            ...(camelizeKeys(refreshResult.data) as LoginResponse),
            ...(typeof expiresIn === "number" ? { expiresIn } : {}),
          }),
        );
        return { data: camelizeKeys(refreshResult.data) as LoginResponse };
      }

      if (refreshResult.error?.status === 401) {
        api.dispatch(logout());
      }
      return { error: refreshResult.error! };
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const token = selectAccessToken(api.getState() as RootState);
  const expiresAt = selectTokenExpiresAt(api.getState() as RootState);
  if (token && isTokenExpired(expiresAt)) {
    const refreshed = await refreshAccessToken(api, extraOptions);
    if ("error" in refreshed) {
      return refreshed;
    }
  }

  if (refreshPromise) {
    const refreshed = await refreshPromise;
    if ("error" in refreshed) {
      return refreshed;
    }
  }

  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshed = await refreshAccessToken(api, extraOptions);
    if ("error" in refreshed) {
      return refreshed;
    }
    result = await baseQuery(args, api, extraOptions);
  }

  return {
    ...result,
    data: camelizeKeys(result.data) as any,
  };
};

const verifiedHotelFormData = (payload: VerifiedHotelPayload) => {
  const formData = new FormData();
  const fields: Array<[string, unknown]> = [
    ["name", payload.name],
    ["primary_source", payload.primarySource],
    ["primary_operator_code", payload.primaryOperatorCode],
    ...(payload.hotelMappingId
      ? [["hotel_mapping_id", payload.hotelMappingId] as [string, unknown]]
      : []),
    ["offer_type", payload.offerType],
    ["country", payload.country],
    ["region", payload.region],
    ["city", payload.city],
    ["stars", payload.stars || ""],
    ["teaser", payload.teaser],
    ["description_html", payload.descriptionHtml],
    ["visited_on", payload.visitedOn || ""],
    ["visited_by", payload.visitedBy],
    ["aeo_summary", payload.aeoSummary],
    ["published", payload.published],
    ["featured", payload.featured],
    ["recommended", payload.recommended],
    ["position", payload.position],
  ];

  fields.forEach(([name, value]) =>
    formData.append(`verified_hotel[${name}]`, String(value)),
  );
  payload.merlinxIds.forEach(sourceId =>
    formData.append("verified_hotel[merlinx_ids][]", sourceId),
  );
  payload.giataIds.forEach(sourceId =>
    formData.append("verified_hotel[giata_ids][]", sourceId),
  );
  if (payload.coverImage)
    formData.append("verified_hotel[cover_image]", payload.coverImage);
  payload.faqs.forEach((faq, index) => {
    const prefix = `verified_hotel[faqs_attributes][${index}]`;
    if (faq.id) formData.append(`${prefix}[id]`, String(faq.id));
    formData.append(`${prefix}[question]`, faq.question);
    formData.append(`${prefix}[answer_html]`, faq.answerHtml);
    formData.append(`${prefix}[active]`, String(faq.active));
    formData.append(`${prefix}[position]`, String(index));
    if (faq._destroy) formData.append(`${prefix}[_destroy]`, "true");
  });
  return formData;
};

const snakeShortLink = (payload: ShortLinkPayload) => ({
  label: payload.label,
  slug: payload.slug,
  destination_url: payload.destinationUrl,
  active: payload.active,
  expires_at: payload.expiresAt || null,
});

const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Section",
    "Tabs",
    "Articles",
    "Article",
    "Categories",
    "Clients",
    "Notes",
    "Offers",
    "Reservations",
    "Statuses",
    "Users",
    "Quizes",
    "Questions",
    "Awards",
    "Rewards",
    "MobileOffers",
    "SolvedQuizes",
    "Consultations",
    "Prizes",
    "GuideEntries",
    "ChatSettings",
    "ChatHandoffs",
    "VerifiedHotels",
    "ShortLinks",
  ],
  keepUnusedDataFor: 0,
  endpoints: builder => ({
    getVerifiedHotels: builder.query<
      VerifiedHotel[],
      { query?: string; published?: string; featured?: string; source?: string }
    >({
      query: params => ({ url: "/admin/verified_hotels", params }),
      providesTags: ["VerifiedHotels"],
    }),
    getVerifiedHotel: builder.query<VerifiedHotel, number>({
      query: id => `/admin/verified_hotels/${id}`,
      providesTags: (_result, _error, id) => [{ type: "VerifiedHotels", id }],
    }),
    createVerifiedHotel: builder.mutation<VerifiedHotel, VerifiedHotelPayload>({
      query: payload => ({
        url: "/admin/verified_hotels",
        method: "POST",
        body: verifiedHotelFormData(payload),
      }),
      invalidatesTags: ["VerifiedHotels"],
    }),
    updateVerifiedHotel: builder.mutation<
      VerifiedHotel,
      { id: number; payload: VerifiedHotelPayload }
    >({
      query: ({ id, payload }) => ({
        url: `/admin/verified_hotels/${id}`,
        method: "PATCH",
        body: verifiedHotelFormData(payload),
      }),
      invalidatesTags: (_result, _error, arg) => [
        "VerifiedHotels",
        { type: "VerifiedHotels", id: arg.id },
      ],
    }),
    deleteVerifiedHotel: builder.mutation<void, number>({
      query: id => ({ url: `/admin/verified_hotels/${id}`, method: "DELETE" }),
      invalidatesTags: ["VerifiedHotels"],
    }),
    searchHotelSources: builder.query<
      HotelSourceResult[],
      { source: "merlinx" | "giata"; q: string; region_id?: number }
    >({
      query: params => ({
        url: "/admin/hotel_sources/search",
        params: { ...params, region_id: params.region_id ?? undefined },
      }),
    }),
    getShortLinks: builder.query<
      ShortLink[],
      { query?: string; active?: string }
    >({
      query: params => ({ url: "/admin/short_links", params }),
      providesTags: ["ShortLinks"],
    }),
    createShortLink: builder.mutation<ShortLink, ShortLinkPayload>({
      query: shortLink => ({
        url: "/admin/short_links",
        method: "POST",
        body: { short_link: snakeShortLink(shortLink) },
      }),
      invalidatesTags: ["ShortLinks"],
    }),
    updateShortLink: builder.mutation<
      ShortLink,
      { id: number; payload: ShortLinkPayload }
    >({
      query: ({ id, payload }) => ({
        url: `/admin/short_links/${id}`,
        method: "PATCH",
        body: { short_link: snakeShortLink(payload) },
      }),
      invalidatesTags: ["ShortLinks"],
    }),
    deleteShortLink: builder.mutation<void, number>({
      query: id => ({ url: `/admin/short_links/${id}`, method: "DELETE" }),
      invalidatesTags: ["ShortLinks"],
    }),
    getShortLinkStats: builder.query<
      ShortLinkStats,
      { id: number; from?: string; to?: string }
    >({
      query: ({ id, ...params }) => ({
        url: `/admin/short_links/${id}/stats`,
        params,
      }),
      providesTags: ["ShortLinks"],
    }),
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: credentials => ({
        url: "/admin/sessions",
        method: "POST",
        body: credentials,
      }),
    }),
    changePassword: builder.mutation<StandardResponse, ChangePasswordRequest>({
      query: data => ({
        url: "/admin/change_password",
        method: "POST",
        body: data,
      }),
    }),
    resetPassword: builder.mutation<StandardResponse, ResetPasswordRequest>({
      query: data => ({
        url: "/admin/password_resets",
        method: "POST",
        body: data,
      }),
    }),
    setPassword: builder.mutation<StandardResponse, SetPasswordRequest>({
      query: data => ({
        url: "/admin/password_resets",
        method: "PATCH",
        body: data,
      }),
    }),
    getMe: builder.query<MeResponse, void>({
      query: () => ({
        url: "/admin/me",
        method: "GET",
      }),
    }),
    changeSectionTitle: builder.mutation<
      StandardResponse,
      { sectionType: SectionType | string; title: string }
    >({
      query: data => ({
        url: `/admin/homepage_sections/${data.sectionType}`,
        method: "PATCH",
        body: { title: data.title },
      }),
      invalidatesTags: (result, _error, arg) =>
        result ? [{ type: "Section", id: arg.sectionType }] : [],
    }),
    deleteHomepageSectionEntry: builder.mutation({
      query: ({ id }: { id: string; sectionType: SectionType | string }) => ({
        url: `/admin/homepage_section_entries/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, args) => [
        { type: "Section", id: args.sectionType },
      ],
    }),
    setHomepageSectionEntryActive: builder.mutation({
      query: ({ id, active }: { id: string; active: boolean }) => ({
        url: `/admin/homepage_section_entries/${id}`,
        method: "PATCH",
        body: { active },
      }),
      invalidatesTags: result => (result ? [{ type: "Section" }] : []),
    }),
    getHomepageSection: builder.query<Section, SectionType | string>({
      query: sectionType => ({
        url: `homepage_sections/${sectionType}`,
        method: "GET",
      }),
      providesTags: (result, _, sectionType) =>
        result ? [{ type: "Section", id: sectionType }] : [],
    }),
    getRegions: builder.query<RegionOption[], void>({
      query: () => ({ url: "", method: "GET" }),
      transformResponse: (response: { regions?: ApiRegion[] }) =>
        (response.regions || []).flatMap(region => [
          { id: region.id, name: region.name, group: "" },
          ...(region.subregions || []).map(sub => ({
            id: sub.id,
            name: sub.name,
            group: region.name,
          })),
        ]),
    }),
    getCustomSections: builder.query<CustomSection[], void>({
      query: () => ({
        url: "custom_homepage_sections",
        method: "GET",
        params: { include_hidden: true },
      }),
      providesTags: result =>
        result ? [{ type: "Section", id: "CUSTOM_LIST" }] : [],
    }),
    // Kolejność i widoczność wszystkich sekcji strony głównej - wbudowanych
    // i custom razem. Bez kafelków, więc tanie do odświeżania.
    getHomepageLayout: builder.query<SectionLayout[], void>({
      query: () => ({
        url: "homepage_sections_layout",
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "Section", id: "LAYOUT" }] : [],
    }),
    reorderHomepageSections: builder.mutation<
      SectionLayout[],
      { order: string[] }
    >({
      query: ({ order }) => ({
        url: "/admin/homepage_sections/reorder",
        method: "POST",
        body: { order },
      }),
      invalidatesTags: [
        { type: "Section", id: "LAYOUT" },
        { type: "Section", id: "CUSTOM_LIST" },
      ],
    }),
    reorderSectionEntries: builder.mutation<
      Section,
      { sectionType: SectionType | string; order: number[] }
    >({
      query: ({ sectionType, order }) => ({
        url: `/admin/homepage_sections/${sectionType}/entries/reorder`,
        method: "POST",
        body: { order },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Section", id: arg.sectionType },
      ],
    }),
    createCustomSection: builder.mutation<CustomSection, { title: string }>({
      query: data => ({
        url: "/admin/homepage_sections",
        method: "POST",
        body: { homepage_section: { title: data.title } },
      }),
      invalidatesTags: [{ type: "Section", id: "CUSTOM_LIST" }],
    }),
    updateCustomSection: builder.mutation<
      CustomSection,
      {
        sectionType: string;
        title?: string;
        position?: number;
        active?: boolean;
      }
    >({
      query: ({ sectionType, ...changes }) => ({
        url: `/admin/homepage_sections/${sectionType}`,
        method: "PATCH",
        body: { homepage_section: changes },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "Section", id: "CUSTOM_LIST" },
        { type: "Section", id: arg.sectionType },
      ],
    }),
    deleteCustomSection: builder.mutation<void, { sectionType: string }>({
      query: ({ sectionType }) => ({
        url: `/admin/homepage_sections/${sectionType}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Section", id: "CUSTOM_LIST" }],
    }),
    getHomepageSectionTabs: builder.query<HomepageSectionTab[], SectionType>({
      query: sectionType => ({
        url: `/admin/homepage_sections/${sectionType}/tabs`,
      }),
      providesTags: (result, _error, arg) =>
        result ? [{ type: "Tabs", id: arg }] : [],
    }),
    addEntry: builder.mutation<void, AddEntryRequest>({
      query: data => {
        const formData = new FormData();

        if (data.category)
          formData.append("homepage_section_entry[category]", data.category);

        if (data.dimensions)
          formData.append(
            "homepage_section_entry[dimensions]",
            data.dimensions,
          );

        if (data.destination)
          formData.append(
            "homepage_section_entry[destination]",
            data.destination,
          );

        if (data.description)
          formData.append(
            "homepage_section_entry[description]",
            data.description,
          );

        if (data.tab)
          formData.append(
            "homepage_section_entry[homepage_section_tab_id]",
            data.tab as unknown as string,
          );

        if (data.season)
          formData.append("homepage_section_entry[season]", data.season);

        formData.append("homepage_section_entry[link]", data.offerUrl);

        if (data.image)
          formData.append("homepage_section_entry[image]", data.image);

        return {
          url: `/admin/homepage_sections/${data.sectionType}/entries`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, _, args) =>
        result ? [{ type: "Section", id: args.sectionType }] : [],
    }),
    editEntry: builder.mutation<void, EditEntryRequest>({
      query: (data: EditEntryRequest) => {
        const formData = new FormData();

        if (data.category)
          formData.append("homepage_section_entry[category]", data.category);

        if (data.dimensions)
          formData.append(
            "homepage_section_entry[dimensions]",
            data.dimensions,
          );

        if (data.destination)
          formData.append(
            "homepage_section_entry[destination]",
            data.destination,
          );

        if (data.description)
          formData.append(
            "homepage_section_entry[description]",
            data.description,
          );

        if (data.tab)
          formData.append(
            "homepage_section_entry[homepage_section_tab_id]",
            data.tab as unknown as string,
          );

        if (data.season)
          formData.append("homepage_section_entry[season]", data.season);

        if (data.offerUrl)
          formData.append("homepage_section_entry[link]", data.offerUrl);

        if (data.image)
          formData.append("homepage_section_entry[image]", data.image);

        return {
          url: `/admin/homepage_section_entries/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: (result, _, args) =>
        result ? [{ type: "Section", id: args.sectionType }] : [],
    }),
    createTab: builder.mutation<void, CreateTabRequest>({
      query: ({ sectionType, title, icon, iconColor }) => ({
        url: `/admin/homepage_sections/${sectionType}/tabs`,
        method: "POST",
        body: {
          title,
          icon,
          icon_color: iconColor,
        },
      }),
      invalidatesTags: (result, _error, arg) =>
        result ? [{ type: "Tabs", id: arg.sectionType }] : [],
    }),
    updateTab: builder.mutation({
      query: ({
        id,
        title,
        active,
        icon,
        iconColor,
      }: {
        id: string;
        title?: string;
        active?: boolean;
        icon?: EntryTabIcon;
        iconColor?: EntryTabIconColor;
      }) => ({
        url: `/admin/homepage_section_tabs/${id}`,
        method: "PATCH",
        body: {
          title,
          active,
          icon,
          icon_color: iconColor,
        },
      }),
      invalidatesTags: result => (result ? [{ type: "Tabs" }] : []),
    }),
    deleteTab: builder.mutation({
      query: ({ id }: { id: string; sectionType: SectionType | string }) => ({
        url: `/admin/homepage_section_tabs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, args) => [
        { type: "Tabs", id: args.sectionType },
        { type: "Section", id: args.sectionType },
      ],
    }),
    createBlogArticle: builder.mutation<void, AddBlogArticleRequest>({
      query: (data: AddBlogArticleRequest) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.shortTitle) formData.append("short_title", data.shortTitle);
        if (data.description) formData.append("description", data.description);
        if (data.content) formData.append("content", data.content);
        if (data.seoLink) formData.append("seo_link", data.seoLink);
        if (data.articleCategoryId)
          formData.append(
            "article_category_id",
            data.articleCategoryId.toString(),
          );
        if (data.mainArticleImage)
          formData.append("main_article_image", data.mainArticleImage);
        if (data.relatedLinks)
          for (let i = 0; i < data.relatedLinks.length; i += 1) {
            formData.append("related_links[]", data.relatedLinks[i]);
          }
        if (data.articleRelations)
          for (let i = 0; i < data.articleRelations.length; i += 1) {
            formData.append("article_relations[]", data.articleRelations[i]);
          }
        return { url: `/admin/articles`, method: "POST", body: formData };
      },
      invalidatesTags: result => (result ? [{ type: "Articles" }] : []),
    }),
    updateBlogArticle: builder.mutation<void, EditBlogArticleRequest>({
      query: (data: EditBlogArticleRequest) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.shortTitle) formData.append("short_title", data.shortTitle);
        if (data.description) formData.append("description", data.description);
        if (data.content) formData.append("content", data.content);
        if (data.seoLink) formData.append("seo_link", data.seoLink);
        if (data.articleCategoryId)
          formData.append(
            "article_category_id",
            data.articleCategoryId.toString(),
          );
        if (data.mainArticleImage)
          formData.append("main_article_image", data.mainArticleImage);
        if (data.relatedLinks)
          for (let i = 0; i < data.relatedLinks.length; i += 1) {
            formData.append("related_links[]", data.relatedLinks[i]);
          }
        if (data.articleRelations)
          for (let i = 0; i < data.articleRelations.length; i += 1) {
            formData.append("article_relations[]", data.articleRelations[i]);
          }
        return {
          url: `/admin/articles/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Articles" }] : []),
    }),
    deleteBlogArticle: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/articles/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Articles" }],
    }),
    getBlogArticles: builder.query<
      { data: BlogArticle[]; totalCount: number },
      IBlogArticleParams
    >({
      query: (params: IBlogArticleParams) => ({
        url: `/admin/articles`,
        method: "GET",
        params: cleanObject(params),
      }),
      transformResponse: (data: BlogArticle[], meta: FetchBaseQueryMeta) => {
        const totalCount = Number(meta?.response?.headers.get("X-Total-Count"));
        return {
          data,
          totalCount,
        };
      },
      providesTags: result => (result ? [{ type: "Articles" }] : []),
    }),
    getSingleBlogArticle: builder.query<
      BlogArticle,
      { id: string | undefined }
    >({
      query: ({ id }) => ({
        url: `/admin/articles/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Articles" }] : []),
    }),
    getGuideEntries: builder.query<
      { data: GuideEntry[]; totalCount: number },
      GuideEntryParams
    >({
      query: params => ({
        url: "/admin/guide_entries",
        method: "GET",
        params: cleanObject(params),
      }),
      transformResponse: (data: GuideEntry[], meta: FetchBaseQueryMeta) => ({
        data,
        totalCount: Number(meta?.response?.headers.get("X-Total-Count") || 0),
      }),
      providesTags: result => (result ? [{ type: "GuideEntries" }] : []),
    }),
    getGuideEntry: builder.query<GuideEntry, number>({
      query: id => ({ url: `/admin/guide_entries/${id}`, method: "GET" }),
      providesTags: (_, __, id) => [{ type: "GuideEntries", id }],
    }),
    createGuideEntry: builder.mutation<GuideEntry, GuideEntryPayload>({
      query: data => ({
        url: "/admin/guide_entries",
        method: "POST",
        body: {
          guide_entry: {
            name: data.name,
            api_name: data.apiName,
            description: data.description,
            published: data.published,
          },
        },
      }),
      invalidatesTags: [{ type: "GuideEntries" }],
    }),
    updateGuideEntry: builder.mutation<
      GuideEntry,
      GuideEntryPayload & { id: number }
    >({
      query: ({ id, ...data }) => ({
        url: `/admin/guide_entries/${id}`,
        method: "PATCH",
        body: {
          guide_entry: {
            name: data.name,
            api_name: data.apiName,
            description: data.description,
            published: data.published,
          },
        },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "GuideEntries" },
        { type: "GuideEntries", id },
      ],
    }),
    deleteGuideEntry: builder.mutation<void, number>({
      query: id => ({ url: `/admin/guide_entries/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "GuideEntries" }],
    }),
    createArticleCategory: builder.mutation<void, AddArticleCategoryRequest>({
      query: ({ name, seoLink }) => ({
        url: `/admin/article_categories`,
        method: "POST",
        body: { name, seo_link: seoLink },
      }),
      invalidatesTags: result => (result ? [{ type: "Categories" }] : []),
    }),
    editArticleCategory: builder.mutation<void, EditArticleCategoryRequest>({
      query: ({ name, id, seoLink }) => ({
        url: `/admin/article_categories/${id}`,
        method: "PATCH",
        body: { name, seo_link: seoLink },
      }),
      invalidatesTags: result => (result ? [{ type: "Categories" }] : []),
    }),
    deleteArticleCategory: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/article_categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Categories" }],
    }),
    getArticleCategories: builder.query<ArticleCategory[], IBaseSearchParams>({
      query: (params: IBaseSearchParams) => ({
        url: `/admin/article_categories`,
        method: "GET",
        params: cleanObject(params),
      }),
      providesTags: result => (result ? [{ type: "Categories" }] : []),
    }),
    getArticleCategory: builder.query<
      ArticleCategory,
      { id: string | undefined }
    >({
      query: ({ id }) => ({
        url: `/admin/article_categories/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Categories" }] : []),
    }),
    addClient: builder.mutation<void, AddClientRequest>({
      query: (data: AddClientRequest) => {
        const formData = new FormData();
        if (data.sex) formData.append("sex", data.sex);
        if (data.firstName) formData.append("first_name", data.firstName);
        if (data.lastName) formData.append("last_name", data.lastName);
        if (data.email) formData.append("email", data.email);
        if (data.phone) formData.append("phone", data.phone);
        if (data.birthday)
          formData.append(
            "birthday",
            dayjs(data.birthday).format("DD/MM/YYYY"),
          );
        if (data.nationality) formData.append("nationality", data.nationality);
        if (data.country) formData.append("country", data.country);
        if (data.city) formData.append("city", data.city);
        if (data.zipCode) formData.append("zip_code", data.zipCode);
        if (data.address) formData.append("address", data.address);
        if (data.reservations) {
          formData.append(
            "reservations_attributes",
            JSON.stringify(data.reservations),
          );
        }
        return { url: `/admin/clients`, method: "POST", body: formData };
      },
      invalidatesTags: result =>
        result ? [{ type: "Clients" }, { type: "Reservations" }] : [],
    }),
    editClient: builder.mutation<void, EditClientRequest>({
      query: (data: EditClientRequest) => {
        const formData = new FormData();
        if (data.sex) formData.append("sex", data.sex);
        if (data.firstName) formData.append("first_name", data.firstName);
        if (data.lastName) formData.append("last_name", data.lastName);
        if (data.email) formData.append("email", data.email);
        if (data.phone) formData.append("phone", data.phone);
        if (data.birthday)
          formData.append(
            "birthday",
            dayjs(data.birthday).format("DD/MM/YYYY"),
          );
        if (data.nationality !== undefined)
          formData.append("nationality", data.nationality);
        if (data.country !== undefined)
          formData.append("country", data.country);
        if (data.city !== undefined) formData.append("city", data.city);
        if (data.zipCode !== undefined)
          formData.append("zip_code", data.zipCode);
        if (data.address !== undefined)
          formData.append("address", data.address);
        if (data.reservations) {
          formData.append(
            "reservations_attributes",
            JSON.stringify(data.reservations),
          );
        }
        return {
          url: `/admin/clients/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Clients" }] : []),
    }),
    deleteClient: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/clients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Clients" }],
    }),
    getClients: builder.query<
      { data: Client[]; totalCount: number },
      INameAndEmailSearchParams
    >({
      query: (params: INameAndEmailSearchParams) => ({
        url: `/admin/clients`,
        method: "GET",
        params: cleanObject(params),
      }),
      transformResponse(data: Client[], meta: FetchBaseQueryMeta) {
        const totalCount = Number(meta?.response?.headers.get("X-Total-Count"));
        return {
          data,
          totalCount,
        };
      },
      providesTags: result => (result ? [{ type: "Clients" }] : []),
    }),
    getSingleClient: builder.query<Client, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/clients/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Clients" }] : []),
    }),
    addClientNote: builder.mutation<void, AddClientNoteRequest>({
      query: (data: AddClientNoteRequest) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.content) formData.append("content", data.content);
        return {
          url: `/admin/clients/${data.clientId}/notes`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Notes" }] : []),
    }),
    editClientNote: builder.mutation<void, EditClientNoteRequest>({
      query: (data: EditClientNoteRequest) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.content) formData.append("content", data.content);
        return {
          url: `/admin/clients/${data.clientId}/notes/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Notes" }] : []),
    }),
    deleteClientNote: builder.mutation({
      query: ({ id, clientId }: { id: string; clientId: string }) => ({
        url: `/admin/clients/${clientId}/notes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Notes" }],
    }),
    getClientNotes: builder.query<
      ClientNote[],
      { clientId: string | undefined }
    >({
      query: ({ clientId }) => ({
        url: `/admin/clients/${clientId}/notes`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Notes" }] : []),
    }),
    getSingleClientNote: builder.query<
      ClientNote,
      { clientId: string | undefined; id: string | undefined }
    >({
      query: ({ clientId, id }) => ({
        url: `/admin/clients/${clientId}/notes/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Notes" }] : []),
    }),
    addClientTourOffer: builder.mutation<void, AddClientTourOfferRequest>({
      query: (data: AddClientTourOfferRequest) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.content) formData.append("content", data.content);
        if (data.file) formData.append("file", data.file);
        return {
          url: `/admin/clients/${data.clientId}/tour_offers`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Offers" }] : []),
    }),
    editClientTourOffer: builder.mutation<void, EditClientTourOfferRequest>({
      query: (data: EditClientTourOfferRequest) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.content) formData.append("content", data.content);
        if (data.file) formData.append("file", data.file);
        return {
          url: `/admin/clients/${data.clientId}/tour_offers/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Offers" }] : []),
    }),
    deleteClientTourOffer: builder.mutation({
      query: ({ id, clientId }: { id: string; clientId: string }) => ({
        url: `/admin/clients/${clientId}/tour_offers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Offers" }],
    }),
    getClientTourOffers: builder.query<
      ClientTourOffer[],
      { clientId: string | undefined }
    >({
      query: ({ clientId }) => ({
        url: `/admin/clients/${clientId}/tour_offers`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Offers" }] : []),
    }),
    getSingleClientTourOffer: builder.query<
      ClientTourOffer,
      { clientId: string | undefined; id: string | undefined }
    >({
      query: ({ clientId, id }) => ({
        url: `/admin/clients/${clientId}/tour_offers/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Offers" }] : []),
    }),
    addClientReservation: builder.mutation<void, AddClientReservation>({
      query: (data: AddClientReservation) => {
        const formData = new FormData();
        if (data.number) formData.append("number", data.number);
        if (data.arrivalDate)
          formData.append(
            "arrival_date",
            dayjs(data.arrivalDate).format("DD/MM/YYYY"),
          );
        if (data.departureDate)
          formData.append(
            "departure_date",
            dayjs(data.departureDate).format("DD/MM/YYYY"),
          );
        if (data.reservationStatusId)
          formData.append(
            "reservation_status_id",
            data.reservationStatusId.toString(),
          );
        if (data.content) {
          formData.append("content", JSON.stringify(data.content));
        }
        return {
          url: `/admin/clients/${data.clientId}/reservations`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result =>
        result ? [{ type: "Reservations" }, { type: "Clients" }] : [],
    }),
    editClientReservation: builder.mutation<void, EditClientReservation>({
      query: (data: EditClientReservation) => {
        const formData = new FormData();

        if (data.number) formData.append("number", data.number);
        if (data.arrivalDate)
          formData.append(
            "arrival_date",
            dayjs(data.arrivalDate).format("DD/MM/YYYY"),
          );
        if (data.departureDate)
          formData.append(
            "departure_date",
            dayjs(data.departureDate).format("DD/MM/YYYY"),
          );
        if (data.reservationStatusId)
          formData.append(
            "reservation_status_id",
            data.reservationStatusId.toString(),
          );
        if (data.content) {
          formData.append("content", JSON.stringify(data.content));
        }
        if (data.paymentContent) {
          formData.append("payment_content", data.paymentContent);
        }

        return {
          url: `/admin/clients/${data.clientId}/reservations/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Reservations" }] : []),
    }),
    deleteClientReservation: builder.mutation({
      query: ({ id, clientId }: { id: string; clientId: string }) => ({
        url: `/admin/clients/${clientId}/reservations/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Reservations" }, { type: "Clients" }],
    }),
    getClientReservations: builder.query<
      ClientReservation[],
      { clientId: string | undefined }
    >({
      query: ({ clientId }) => ({
        url: `/admin/clients/${clientId}/reservations`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Reservations" }] : []),
    }),
    getPaymentStatus: builder.mutation<
      PaymentStatus,
      { paymentId: string | undefined }
    >({
      query: ({ paymentId }) => ({
        url: `/payment/check_status`,
        method: "GET",
        params: { paymentId },
      }),
    }),
    patchRestPayment: builder.mutation<
      PaymentRest,
      { paymentId: string; value: boolean }
    >({
      query: ({ paymentId, value }) => ({
        url: `/payment/set_paid_at`,
        method: "PATCH",
        body: { paymentId, value },
      }),
    }),
    posBookCancel: builder.mutation<BookCancelResponse, BookCancel>({
      query: ({ bookingNumber, operator }) => ({
        url: `/offers/book_cancel`,
        method: "POST",
        body: { booking_number: bookingNumber, operator },
      }),
    }),
    getSingleClientReservation: builder.query<
      ClientReservation,
      { clientId: string | undefined; id: string | undefined }
    >({
      query: ({ clientId, id }) => ({
        url: `/admin/clients/${clientId}/reservations/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Reservations" }] : []),
    }),
    getReservationsStatuses: builder.query<ReservationsStatuses[], void>({
      query: () => ({
        url: `/admin/reservation_statuses`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Statuses" }] : []),
    }),
    getAllReservations: builder.query<
      { data: ClientReservation[]; totalCount: number },
      IReservationsSearchParams
    >({
      query: (params: IReservationsSearchParams) => ({
        url: `/admin/reservations`,
        method: "GET",
        params: cleanObject(params),
      }),
      transformResponse: (
        data: ClientReservation[],
        meta: FetchBaseQueryMeta,
      ) => {
        const totalCount = Number(meta?.response?.headers.get("X-Total-Count"));
        return {
          data,
          totalCount,
        };
      },
      providesTags: result => (result ? [{ type: "Reservations" }] : []),
    }),
    addAppUser: builder.mutation<void, AddAppUser>({
      query: (data: AddAppUser) => {
        const formData = new FormData();
        if (data.firstName) formData.append("first_name", data.firstName);
        if (data.lastName) formData.append("last_name", data.lastName);
        if (data.email) formData.append("email", data.email);
        if (data.phone) formData.append("phone", data.phone);
        return {
          url: `/admin/mobile_users`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Users" }] : []),
    }),
    editAppUser: builder.mutation<void, EditAppUser>({
      query: (data: EditAppUser) => {
        const formData = new FormData();
        if (data.firstName) formData.append("first_name", data.firstName);
        if (data.lastName) formData.append("last_name", data.lastName);
        if (data.email) formData.append("email", data.email);
        if (data.phone) formData.append("phone", data.phone);
        return {
          url: `/admin/mobile_users/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Users" }] : []),
    }),
    deleteAppUser: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/mobile_users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Users" }],
    }),
    getAppUsers: builder.query<
      { data: AppUser[]; totalCount: number },
      INameAndEmailSearchParams
    >({
      query: (params: INameAndEmailSearchParams) => ({
        url: `/admin/mobile_users`,
        method: "GET",
        params: cleanObject(params),
      }),
      transformResponse: (data: AppUser[], meta: FetchBaseQueryMeta) => {
        const totalCount = Number(meta?.response?.headers.get("X-Total-Count"));
        return {
          data,
          totalCount,
        };
      },
      providesTags: result => (result ? [{ type: "Users" }] : []),
    }),
    getAppUser: builder.query<AppUser, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/mobile_users/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Users" }] : []),
    }),
    addQuiz: builder.mutation<void, AddQuiz>({
      query: (data: AddQuiz) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.image) formData.append("image", data.image);
        if (data.questions) {
          formData.append("questions", JSON.stringify(data.questions));
        }
        return {
          url: `/admin/quizzes`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Quizes" }] : []),
    }),
    editQuiz: builder.mutation<void, EditQuiz>({
      query: (data: EditQuiz) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.image) formData.append("image", data.image);
        if (data.questions) {
          formData.append("questions", JSON.stringify(data.questions));
        }
        return {
          url: `/admin/quizzes/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Quizes" }] : []),
    }),
    deleteQuiz: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/quizzes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Quizes" }],
    }),
    getQuizes: builder.query<
      { data: Quiz[]; totalCount: number },
      IBaseSearchParams
    >({
      query: (params: IBaseSearchParams) => ({
        url: `/admin/quizzes`,
        method: "GET",
        params: cleanObject(params),
      }),
      transformResponse: (data: Quiz[], meta: FetchBaseQueryMeta) => {
        const totalCount = Number(meta?.response?.headers.get("X-Total-Count"));
        return {
          data,
          totalCount,
        };
      },
      providesTags: result => (result ? [{ type: "Quizes" }] : []),
    }),
    getQuiz: builder.query<Quiz, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/quizzes/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Quizes" }] : []),
    }),
    addQuestion: builder.mutation<void, AddQuestion>({
      query: (data: AddQuestion) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.points) formData.append("points", data.points.toString());
        if (data.quizId) formData.append("quiz_id", data.quizId.toString());
        if (data.answers) {
          for (let i = 0; i < data.answers.length; i += 1) {
            formData.append("answers[]", data.answers[i]);
          }
        }
        if (data.correct) {
          if (data.correct.length === 0) {
            formData.append("correct[]", [].toString());
          }
          for (let i = 0; i < data.correct.length; i += 1) {
            formData.append("correct[]", data.correct[i]);
          }
        }
        return {
          url: `/admin/questions`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result =>
        result ? [{ type: "Questions" }, { type: "Quizes" }] : [],
    }),
    editQuestion: builder.mutation<void, EditQuestion>({
      query: (data: EditQuestion) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.points) formData.append("points", data.points.toString());
        if (data.quizId) formData.append("quiz_id", data.quizId.toString());
        if (data.answers) {
          for (let i = 0; i < data.answers.length; i += 1) {
            formData.append("answers[]", data.answers[i]);
          }
        }
        if (data.correct) {
          if (data.correct.length === 0) {
            formData.append("correct[]", [].toString());
          }
          for (let i = 0; i < data.correct.length; i += 1) {
            formData.append("correct[]", data.correct[i]);
          }
        }
        return {
          url: `/admin/questions/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result =>
        result ? [{ type: "Questions" }, { type: "Quizes" }] : [],
    }),
    deleteQuestion: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/questions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Questions" }, { type: "Quizes" }],
    }),
    getQuestions: builder.query<Question[], void>({
      query: () => ({
        url: `/admin/questions`,
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "Questions" }, { type: "Quizes" }] : [],
    }),
    getQuestion: builder.query<Question, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/questions/${id}`,
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "Questions" }, { type: "Quizes" }] : [],
    }),
    addAward: builder.mutation<void, AddAward>({
      query: (data: AddAward) => {
        const formData = new FormData();
        if (data.name) formData.append("name", data.name);
        if (data.description) formData.append("description", data.description);
        if (data.images) {
          for (let i = 0; i < data.images.length; i += 1) {
            formData.append("images[]", data.images[i]);
          }
        }
        if (data.count !== undefined)
          formData.append("count", data.count.toString());
        if (data.points !== undefined)
          formData.append("points", data.points.toString());
        return {
          url: `/admin/awards`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Awards" }] : []),
    }),
    editAward: builder.mutation<void, EditAward>({
      query: (data: EditAward) => {
        const formData = new FormData();
        if (data.name) formData.append("name", data.name);
        if (data.description) formData.append("description", data.description);
        if (data.images) {
          for (let i = 0; i < data.images.length; i += 1) {
            formData.append("images[]", data.images[i]);
          }
        }
        if (data.count !== undefined)
          formData.append("count", data.count.toString());
        if (data.points !== undefined)
          formData.append("points", data.points.toString());
        return {
          url: `/admin/awards/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "Awards" }] : []),
    }),
    deleteAward: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/awards/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Awards" }],
    }),
    getSelectedAwards: builder.query<SelectedAwards, ISelectedAwards>({
      query: (params: ISelectedAwards) => ({
        url: `/admin/mobile_users/show_users_awards`,
        method: "GET",
        params: cleanObject(params),
      }),
    }),
    getCurrentAwards: builder.query<any, { id: string }>({
      query: (params: { id: string }) => ({
        url: `/admin/mobile_users/${params.id}/show_user_awards`,
        method: "GET",
      }),
    }),
    getAwards: builder.query<
      { data: Award[]; totalCount: number },
      IBaseSearchParams
    >({
      query: (params: IBaseSearchParams) => ({
        url: `/admin/awards`,
        method: "GET",
        params: cleanObject({
          limit: PAGINATION_LIMITS.UNLIMITED,
          order_by: "name",
          sort_order: "asc",
          ...params,
        }),
      }),
      transformResponse: (data: Award[], meta: FetchBaseQueryMeta) => {
        const totalCount = Number(meta?.response?.headers.get("X-Total-Count"));
        return {
          data,
          totalCount,
        };
      },
      providesTags: result => (result ? [{ type: "Awards" }] : []),
    }),
    getAward: builder.query<Award, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/awards/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Awards" }] : []),
    }),
    addReward: builder.mutation<void, AddReward>({
      query: (data: AddReward) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.points !== undefined)
          formData.append("points", data.points.toString());
        if (data.mobileUserId)
          formData.append("mobile_user_id", data.mobileUserId.toString());
        return {
          url: `/admin/travel_rewards`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result =>
        result ? [{ type: "Rewards" }, { type: "Users" }] : [],
    }),
    editReward: builder.mutation<void, EditReward>({
      query: (data: EditReward) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.points !== undefined)
          formData.append("points", data.points.toString());
        if (data.mobileUserId)
          formData.append("mobile_user_id", data.mobileUserId.toString());
        return {
          url: `/admin/travel_rewards/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result =>
        result ? [{ type: "Rewards" }, { type: "Users" }] : [],
    }),
    deleteReward: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/travel_rewards/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Rewards" }, { type: "Users" }],
    }),
    getRewards: builder.query<Reward[], void>({
      query: () => ({
        url: `/admin/travel_rewards`,
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "Rewards" }, { type: "Users" }] : [],
    }),
    getReward: builder.query<Reward, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/travel_rewards/${id}`,
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "Rewards" }, { type: "Users" }] : [],
    }),
    addMobileOffer: builder.mutation<void, AddMobileOffer>({
      query: (data: AddMobileOffer) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.description) formData.append("description", data.description);
        if (data.link) formData.append("link", data.link);
        if (data.image) {
          formData.append("image", data.image);
        }
        return {
          url: `/admin/mobile_offer_entries`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "MobileOffers" }] : []),
    }),
    editMobileOffer: builder.mutation<void, EditMobileOffer>({
      query: (data: EditMobileOffer) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.description) formData.append("description", data.description);
        if (data.link) formData.append("link", data.link);
        if (data.image) {
          formData.append("image", data.image);
        }
        return {
          url: `/admin/mobile_offer_entries/${data.id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: result => (result ? [{ type: "MobileOffers" }] : []),
    }),
    deleteMobileOffer: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/mobile_offer_entries/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "MobileOffers" }],
    }),
    getMobileOffers: builder.query<
      { data: MobileOffer[]; totalCount: number },
      IBaseSearchParams
    >({
      query: (params: IBaseSearchParams) => ({
        url: `/admin/mobile_offer_entries`,
        method: "GET",
        params: cleanObject(params),
      }),
      transformResponse: (data: MobileOffer[], meta: FetchBaseQueryMeta) => {
        const totalCount = Number(meta?.response?.headers.get("X-Total-Count"));
        return {
          data,
          totalCount,
        };
      },
      providesTags: result => (result ? [{ type: "MobileOffers" }] : []),
    }),
    getMobileOffer: builder.query<MobileOffer, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/mobile_offer_entries/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "MobileOffers" }] : []),
    }),
    deleteSolvedQuiz: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/quiz_results/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "SolvedQuizes" }, { type: "Users" }],
    }),
    getSolvedQuizes: builder.query<SolvedQuiz[], void>({
      query: () => ({
        url: `/admin/quiz_results`,
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "SolvedQuizes" }, { type: "Users" }] : [],
    }),
    getSolvedQuiz: builder.query<SolvedQuiz, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/quiz_results/${id}`,
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "SolvedQuizes" }, { type: "Users" }] : [],
    }),
    changeStatus: builder.mutation<
      void,
      {
        id: string | undefined;
        status: boolean;
        param: string;
        section: RefetchSection;
      }
    >({
      query: ({ id, status, param }) => ({
        url: `/admin/${param}/${id}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, _, arg) =>
        result ? [{ type: `${arg.section}` }] : [],
    }),
    addConsultation: builder.mutation<void, AddConsultation>({
      query: (data: AddConsultation) => {
        return {
          url: `/admin/consulting_calendars`,
          method: "POST",
          body: {
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            phone: data.phone,
            meeting_date: dayjs(data.meetingDate).format("YYYY-MM-DD HH:mm"),
          },
        };
      },
      invalidatesTags: result => (result ? [{ type: "Consultations" }] : []),
    }),
    editConsultation: builder.mutation<void, EditConsultation>({
      query: (data: EditConsultation) => {
        return {
          url: `/admin/consulting_calendars/${data.id}`,
          method: "PATCH",
          body: {
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            phone: data.phone,
            meeting_date: dayjs(data.meetingDate).format("YYYY-MM-DD HH:mm"),
          },
        };
      },
      invalidatesTags: result => (result ? [{ type: "Consultations" }] : []),
    }),
    deleteConsultation: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/consulting_calendars/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Consultations" }],
    }),
    getConsultations: builder.query<
      { data: Consultation[]; totalCount: number },
      INameAndEmailSearchParams
    >({
      query: (params: INameAndEmailSearchParams) => ({
        url: `/admin/consulting_calendars`,
        method: "GET",
        params: cleanObject(params),
      }),
      transformResponse: (data: Consultation[], meta: FetchBaseQueryMeta) => {
        const totalCount = Number(meta?.response?.headers.get("X-Total-Count"));
        return {
          data,
          totalCount,
        };
      },
      providesTags: result => (result ? [{ type: "Consultations" }] : []),
    }),
    getConsultation: builder.query<Consultation, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/consulting_calendars/${id}`,
        method: "GET",
      }),
      providesTags: result => (result ? [{ type: "Consultations" }] : []),
    }),
    addPrize: builder.mutation<void, AddPrize>({
      query: (data: AddPrize) => {
        return {
          url: `/admin/award_orders`,
          method: "POST",
          body: {
            mobile_user_id: data.mobileUserId,
            award_id: data.awardId,
          },
        };
      },
      invalidatesTags: result =>
        result ? [{ type: "Prizes" }, { type: "Users" }] : [],
    }),
    deletePrize: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `/admin/award_orders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Prizes" }, { type: "Users" }],
    }),
    getPrizes: builder.query<Prize[], void>({
      query: () => ({
        url: `/admin/award_orders`,
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "Prizes" }, { type: "Users" }] : [],
    }),
    getPrize: builder.query<Prize, { id: string | undefined }>({
      query: ({ id }) => ({
        url: `/admin/award_orders/${id}`,
        method: "GET",
      }),
      providesTags: result =>
        result ? [{ type: "Prizes" }, { type: "Users" }] : [],
    }),
    pushToSingle: builder.mutation<unknown, PushToAll>({
      query: (data: PushToAll) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.body) formData.append("body", data.body);
        if (data.image) formData.append("image", data.image);
        if (data.link_url) formData.append("link_url", data.link_url);
        if (data.mobile_user_id)
          formData.append("mobile_user_id", data.mobile_user_id.toString());
        return {
          url: "/admin/notifications/single",
          method: "POST",
          body: formData,
        };
      },
    }),
    pushToAll: builder.mutation<unknown, PushToAll>({
      query: (data: PushToAll) => {
        const formData = new FormData();
        if (data.title) formData.append("title", data.title);
        if (data.body) formData.append("body", data.body);
        if (data.image) formData.append("image", data.image);
        if (data.link_url) formData.append("link_url", data.link_url);
        return {
          url: "/admin/notifications/all",
          method: "POST",
          body: formData,
        };
      },
    }),
    sendReview: builder.mutation<void, any>({
      query: (id: string) => ({
        url: `/admin/reservations/${id}/request_review`,
        method: "POST",
      }),
      invalidatesTags: () => [{ type: "Reservations" }],
    }),
    getChatSettings: builder.query<ChatSettings, void>({
      query: () => ({ url: "/admin/chat/settings", method: "GET" }),
      providesTags: ["ChatSettings"],
    }),
    updateChatSettings: builder.mutation<
      ChatSettings,
      {
        enabled?: boolean;
        model?: string;
        apiKey?: string;
        currentPassword?: string;
      }
    >({
      query: data => ({
        url: "/admin/chat/settings",
        method: "PATCH",
        body: {
          enabled: data.enabled,
          model: data.model,
          api_key: data.apiKey,
          current_password: data.currentPassword,
        },
      }),
      invalidatesTags: ["ChatSettings"],
    }),
    testChatApiKey: builder.mutation<
      { ok: boolean; models: string[] },
      { apiKey: string }
    >({
      query: data => ({
        url: "/admin/chat/settings/test_key",
        method: "POST",
        body: { api_key: data.apiKey },
      }),
    }),
    getChatModels: builder.query<{ models: string[] }, void>({
      query: () => ({ url: "/admin/chat/models", method: "GET" }),
      providesTags: ["ChatSettings"],
    }),
    testChatModel: builder.mutation<{ ok: boolean; model: string }, string>({
      query: model => ({
        url: "/admin/chat/models/test",
        method: "POST",
        body: { model },
      }),
      invalidatesTags: ["ChatSettings"],
    }),
    deleteChatApiKey: builder.mutation<void, string>({
      query: currentPassword => ({
        url: "/admin/chat/settings/api_key",
        method: "DELETE",
        body: { current_password: currentPassword },
      }),
      invalidatesTags: ["ChatSettings"],
    }),
    getChatHandoffs: builder.query<ChatHandoffsResponse, ChatHandoffListParams>(
      {
        query: ({ page, limit, query, status, orderBy, sortOrder }) => ({
          url: "/admin/chat/handoffs",
          method: "GET",
          params: cleanObject({
            page,
            limit,
            query,
            status,
            order_by: orderBy,
            sort_order: sortOrder,
          }),
        }),
        transformResponse: (data: ChatHandoff[], meta: FetchBaseQueryMeta) => ({
          data,
          totalCount: Number(
            meta?.response?.headers.get("X-Total-Count") || data.length,
          ),
        }),
        providesTags: ["ChatHandoffs"],
      },
    ),
    getChatHandoff: builder.query<ChatHandoff, number>({
      query: id => ({ url: `/admin/chat/handoffs/${id}`, method: "GET" }),
      providesTags: (_result, _error, id) => [{ type: "ChatHandoffs", id }],
    }),
    updateChatHandoff: builder.mutation<
      ChatHandoff,
      { id: number; status: ChatHandoff["status"] }
    >({
      query: data => ({
        url: `/admin/chat/handoffs/${data.id}`,
        method: "PATCH",
        body: { status: data.status },
      }),
      invalidatesTags: (_result, _error, data) => [
        { type: "ChatHandoffs", id: data.id },
      ],
    }),
  }),
});

export const {
  useLoginMutation,
  useResetPasswordMutation,
  useSetPasswordMutation,
  useGetMeQuery,
  useChangeSectionTitleMutation,
  useGetHomepageSectionQuery,
  useChangePasswordMutation,
  useDeleteHomepageSectionEntryMutation,
  useSetHomepageSectionEntryActiveMutation,
  useGetHomepageSectionTabsQuery,
  useCreateTabMutation,
  usePushToSingleMutation,
  usePushToAllMutation,
  useUpdateTabMutation,
  useDeleteTabMutation,
  useAddEntryMutation,
  useEditEntryMutation,
  useCreateBlogArticleMutation,
  useUpdateBlogArticleMutation,
  useDeleteBlogArticleMutation,
  useGetBlogArticlesQuery,
  useCreateArticleCategoryMutation,
  useDeleteArticleCategoryMutation,
  useGetArticleCategoriesQuery,
  useGetArticleCategoryQuery,
  useEditArticleCategoryMutation,
  useGetSingleBlogArticleQuery,
  useGetGuideEntriesQuery,
  useGetGuideEntryQuery,
  useCreateGuideEntryMutation,
  useUpdateGuideEntryMutation,
  useDeleteGuideEntryMutation,
  useAddClientMutation,
  useEditClientMutation,
  useDeleteClientMutation,
  useGetClientsQuery,
  useGetSingleClientQuery,
  useAddClientNoteMutation,
  useEditClientNoteMutation,
  useDeleteClientNoteMutation,
  useGetClientNotesQuery,
  useGetSingleClientNoteQuery,
  useAddClientTourOfferMutation,
  useEditClientTourOfferMutation,
  useDeleteClientTourOfferMutation,
  useGetClientTourOffersQuery,
  useGetSingleClientTourOfferQuery,
  useAddClientReservationMutation,
  useEditClientReservationMutation,
  useDeleteClientReservationMutation,
  useGetClientReservationsQuery,
  useGetSingleClientReservationQuery,
  useGetReservationsStatusesQuery,
  useGetAllReservationsQuery,
  useAddAppUserMutation,
  useEditAppUserMutation,
  useDeleteAppUserMutation,
  useGetAppUsersQuery,
  useGetAppUserQuery,
  useAddQuizMutation,
  useEditQuizMutation,
  useDeleteQuizMutation,
  useGetQuizesQuery,
  useGetQuizQuery,
  useAddQuestionMutation,
  useEditQuestionMutation,
  useDeleteQuestionMutation,
  useGetQuestionQuery,
  useGetQuestionsQuery,
  useAddAwardMutation,
  useEditAwardMutation,
  useDeleteAwardMutation,
  useGetAwardsQuery,
  useGetSelectedAwardsQuery,
  useGetCurrentAwardsQuery,
  useGetAwardQuery,
  useAddRewardMutation,
  useEditRewardMutation,
  useDeleteRewardMutation,
  useGetRewardsQuery,
  useGetRewardQuery,
  useAddMobileOfferMutation,
  useEditMobileOfferMutation,
  useDeleteMobileOfferMutation,
  useGetMobileOffersQuery,
  useGetMobileOfferQuery,
  useDeleteSolvedQuizMutation,
  useGetSolvedQuizesQuery,
  useGetSolvedQuizQuery,
  useChangeStatusMutation,
  useAddConsultationMutation,
  useEditConsultationMutation,
  useDeleteConsultationMutation,
  useGetConsultationsQuery,
  useGetConsultationQuery,
  useAddPrizeMutation,
  useDeletePrizeMutation,
  useGetPrizesQuery,
  useGetPrizeQuery,
  useGetPaymentStatusMutation,
  usePosBookCancelMutation,
  usePatchRestPaymentMutation,
  useSendReviewMutation,
  useGetChatSettingsQuery,
  useUpdateChatSettingsMutation,
  useTestChatApiKeyMutation,
  useGetChatModelsQuery,
  useTestChatModelMutation,
  useDeleteChatApiKeyMutation,
  useGetRegionsQuery,
  useGetCustomSectionsQuery,
  useGetHomepageLayoutQuery,
  useReorderHomepageSectionsMutation,
  useReorderSectionEntriesMutation,
  useCreateCustomSectionMutation,
  useUpdateCustomSectionMutation,
  useDeleteCustomSectionMutation,
  useGetChatHandoffsQuery,
  useGetChatHandoffQuery,
  useUpdateChatHandoffMutation,
  useGetVerifiedHotelsQuery,
  useGetVerifiedHotelQuery,
  useCreateVerifiedHotelMutation,
  useUpdateVerifiedHotelMutation,
  useDeleteVerifiedHotelMutation,
  useLazySearchHotelSourcesQuery,
  useSearchHotelSourcesQuery,
  useGetShortLinksQuery,
  useCreateShortLinkMutation,
  useUpdateShortLinkMutation,
  useDeleteShortLinkMutation,
  useLazyGetShortLinkStatsQuery,
} = api;
export default api;
